import React, { useState, useEffect, useCallback } from "react";
import { 
  FiTrendingUp, 
  FiSettings, 
  FiActivity, 
  FiUsers, 
  FiRefreshCw,
  FiAlertCircle
} from "react-icons/fi";
import LoadingScreen from "../utilities/LoadingScreen";
import { useReferralApi } from "./hooks/useReferralApi";
import ReferralStats from "./components/ReferralStats";
import ReferralPercentage from "./components/ReferralPercentage";
import ReferralTransactions from "./components/ReferralTransactions";
import ReferralUsers from "./components/ReferralUsers";
import ReferralChainView from "./components/ReferralChainView";
import ReferralSponsors from "./components/ReferralSponsors";

const TABS = [
  { id: 'overview', label: 'Overview', icon: <FiTrendingUp /> },
  { id: 'settings', label: 'Settings', icon: <FiSettings /> },
  { id: 'transactions', label: 'Transactions', icon: <FiActivity /> },
  { id: 'users', label: 'Users', icon: <FiUsers /> },
  { id: 'sponsors', label: 'Sponsors', icon: <FiUsers /> },
];

const Referal = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [percentage, setPercentageData] = useState(null);
  const [transactions, setTransactions] = useState({ transactions: [], total_count: 0 });
  const [users, setUsersData] = useState({ users: [], total_count: 0 });
  const [sponsorsData, setSponsorsData] = useState({ sponsors: [], total_count: 0 });
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [usersPage, setUsersPage] = useState(1);
  const [sponsorsPage, setSponsorsPage] = useState(1);
  const [initialLoading, setInitialLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  const [error, setError] = useState(null);

  // Chain view state
  const [showChainView, setShowChainView] = useState(false);
  const [chainData, setChainData] = useState(null);
  const [chainLoading, setChainLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const {
    getStats,
    getPercentage,
    setPercentage,
    getTransactions,
    getUsers,
    getUserChain,
    getSponsorsWithReferrals,
    loading: apiLoading
  } = useReferralApi();

  const limit = 10;

  // Load initial data
  const loadInitialData = useCallback(async () => {
    setInitialLoading(true);
    setError(null);
    try {
      const [statsData, percentageData] = await Promise.all([
        getStats().catch(() => null),
        getPercentage().catch(() => null),
      ]);
      
      if (statsData) setStats(statsData);
      if (percentageData) setPercentageData(percentageData);
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setInitialLoading(false);
    }
  }, [getStats, getPercentage]);

  // Load transactions
  const loadTransactions = useCallback(async (page = 1) => {
    setTabLoading(true);
    try {
      const data = await getTransactions(page, limit);
      setTransactions({
        transactions: data?.transactions || [],
        total_count: data?.total_count || 0,
      });
      setTransactionsPage(page);
    } catch (err) {
      console.error('Failed to load transactions:', err);
    } finally {
      setTabLoading(false);
    }
  }, [getTransactions]);

  // Load users
  const loadUsers = useCallback(async (page = 1) => {
    setTabLoading(true);
    try {
      const data = await getUsers(page, limit);
      setUsersData({
        users: data?.users || [],
        total_count: data?.total_count || 0,
      });
      setUsersPage(page);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setTabLoading(false);
    }
  }, [getUsers]);

  // Load sponsors
  const loadSponsors = useCallback(async (page = 1) => {
    setTabLoading(true);
    try {
      const data = await getSponsorsWithReferrals(page, limit);
      setSponsorsData({
        sponsors: data?.sponsors || [],
        total_count: data?.total_count || 0,
      });
      setSponsorsPage(page);
    } catch (err) {
      console.error('Failed to load sponsors with referrals:', err);
    } finally {
      setTabLoading(false);
    }
  }, [getSponsorsWithReferrals]);

  // Load chain for a user
  const loadUserChain = useCallback(async (userId, username) => {
    setSelectedUser({ userId, username });
    setShowChainView(true);
    setChainLoading(true);
    try {
      const data = await getUserChain(userId);
      setChainData(data);
    } catch (err) {
      console.error('Failed to load user chain:', err);
      setChainData(null);
    } finally {
      setChainLoading(false);
    }
  }, [getUserChain]);

  // Handle percentage save
  const handlePercentageSave = async (newPercentage, newSecondLevel, adminId) => {
    await setPercentage(newPercentage, newSecondLevel, adminId);
    // Refresh percentage and stats
    const [newPercentageData, newStats] = await Promise.all([
      getPercentage().catch(() => null),
      getStats().catch(() => null),
    ]);
    if (newPercentageData) setPercentageData(newPercentageData);
    if (newStats) setStats(newStats);
  };

  // Initial load
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Load tab-specific data when tab changes
  const transactionsLen = transactions?.transactions?.length ?? 0;
  const usersLen = users?.users?.length ?? 0;
  const sponsorsLen = sponsorsData?.sponsors?.length ?? 0;

  useEffect(() => {
    if (activeTab === 'transactions' && transactionsLen === 0) {
      loadTransactions(1);
    } else if (activeTab === 'users' && usersLen === 0) {
      loadUsers(1);
    } else if (activeTab === 'sponsors' && sponsorsLen === 0) {
      loadSponsors(1);
    }
  }, [activeTab, transactionsLen, usersLen, sponsorsLen, loadTransactions, loadUsers, loadSponsors]);

  const handleRefresh = () => {
    if (activeTab === 'overview' || activeTab === 'settings') {
      loadInitialData();
    } else if (activeTab === 'transactions') {
      loadTransactions(transactionsPage);
    } else if (activeTab === 'users') {
      loadUsers(usersPage);
    } else if (activeTab === 'sponsors') {
      loadSponsors(sponsorsPage);
    }
  };

  if (initialLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="lg:p-1 min-h-screen lg:mr-[2vw] bg-gray-50">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h2 className="text-3xl font-bold text-indigo-800">Referral Management</h2>
          <p className="text-gray-600 mt-1">Configure and monitor the referral system</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={apiLoading || tabLoading}
          className="mt-4 md:mt-0 flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          <FiRefreshCw className={apiLoading || tabLoading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <FiAlertCircle className="text-red-500 text-xl flex-shrink-0" />
          <div>
            <p className="text-red-800 font-medium">Error loading data</p>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
          <button
            onClick={loadInitialData}
            className="ml-auto px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
          >
            Retry
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2 p-1 bg-white rounded-xl shadow-sm">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-indigo-500 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab.icon}
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <ReferralStats stats={stats} loading={apiLoading} />

            {/* Financial Report (2026-06-05) */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Financial Report</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-lg p-4 border border-emerald-100">
                  <p className="text-sm text-gray-600 mb-1">Gross Entry Fees</p>
                  <p className="text-2xl font-bold text-emerald-700">₹{(stats?.gross_entry_fees ?? 0).toLocaleString('en-IN')}</p>
                  <p className="text-xs text-gray-500 mt-1">total event fees that drove referrals</p>
                </div>
                <div className="bg-gradient-to-br from-rose-50 to-red-50 rounded-lg p-4 border border-rose-100">
                  <p className="text-sm text-gray-600 mb-1">Commission Paid</p>
                  <p className="text-2xl font-bold text-rose-700">₹{(stats?.total_rewards_distributed ?? 0).toLocaleString('en-IN')}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {stats?.gross_entry_fees ? `${(((stats?.commission_event ?? 0) / stats.gross_entry_fees) * 100).toFixed(1)}% referral cost` : 'L1 + L2 + onboarding'}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-lg p-4 border border-indigo-100">
                  <p className="text-sm text-gray-600 mb-1">Net (fees − event commission)</p>
                  <p className="text-2xl font-bold text-indigo-700">₹{(stats?.net_after_commission ?? 0).toLocaleString('en-IN')}</p>
                  <p className="text-xs text-gray-500 mt-1">retained from event entry fees</p>
                </div>
              </div>
              {/* Commission split */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {[
                  { label: 'Level 1', val: stats?.commission_l1, color: 'text-green-600' },
                  { label: 'Level 2', val: stats?.commission_l2, color: 'text-orange-600' },
                  { label: 'Onboarding', val: stats?.commission_onboarding, color: 'text-purple-600' },
                  { label: 'Event', val: stats?.commission_event, color: 'text-pink-600' },
                ].map((c) => (
                  <div key={c.label} className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500 mb-1">{c.label} commission</p>
                    <p className={`text-lg font-bold ${c.color}`}>₹{(c.val ?? 0).toLocaleString('en-IN')}</p>
                  </div>
                ))}
              </div>
              {/* By referrer type */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-100">
                  <p className="text-xs text-gray-500 mb-1">Paid to Users</p>
                  <p className="text-lg font-bold text-blue-700">₹{(stats?.commission_users ?? 0).toLocaleString('en-IN')}</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center border border-amber-100">
                  <p className="text-xs text-gray-500 mb-1">Paid to Sponsors</p>
                  <p className="text-lg font-bold text-amber-700">₹{(stats?.commission_sponsors ?? 0).toLocaleString('en-IN')}</p>
                </div>
              </div>
              {/* Top earners */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Top Earners</p>
                {(stats?.top_earners?.length ?? 0) === 0 ? (
                  <p className="text-sm text-gray-400">No earnings yet.</p>
                ) : (
                  <div className="space-y-2">
                    {stats.top_earners.slice(0, 5).map((e, i) => (
                      <div key={e.user_id || i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                          <span className="text-sm font-medium text-gray-800 truncate">@{e.username || e.user_id}</span>
                        </div>
                        <span className="text-sm font-bold text-emerald-700">₹{(e.earned ?? 0).toLocaleString('en-IN')}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Quick Settings Preview */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Current Settings</h3>
                <button
                  onClick={() => setActiveTab('settings')}
                  className="text-indigo-500 hover:text-indigo-600 text-sm font-medium"
                >
                  Edit Settings →
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Referral Percentage</p>
                  <p className="text-3xl font-bold text-indigo-600">
                    {percentage?.referral_percentage ?? stats?.current_percentage ?? 0}%
                  </p>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">L1 Reward Rate</p>
                  <p className="text-3xl font-bold text-green-600">
                    {percentage?.referral_percentage ?? stats?.current_percentage ?? 0}%
                  </p>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">L2 Reward Rate</p>
                  <p className="text-3xl font-bold text-orange-600">
                    {percentage?.second_level_percentage ?? 1}%
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ReferralPercentage
              currentPercentage={percentage?.referral_percentage ?? stats?.current_percentage ?? 0}
              currentSecondLevel={percentage?.second_level_percentage ?? 1}
              onSave={handlePercentageSave}
              loading={apiLoading}
              lastUpdated={percentage?.updated_at}
              lastUpdatedBy={percentage?.updated_by}
            />
            
            {/* Additional Settings Info */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Referral Rules</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-sm flex-shrink-0">
                    L1
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Level 1 - Direct Referrer</p>
                    <p className="text-sm text-gray-600">
                      Gets {percentage?.referral_percentage ?? 0}% of the event entry fee when their referred user participates
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold text-sm flex-shrink-0">
                    L2
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Level 2 - Indirect Referrer</p>
                    <p className="text-sm text-gray-600">
                      Gets {percentage?.second_level_percentage ?? 1}% of the event entry fee (the referrer's referrer)
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 text-lg flex-shrink-0">
                    🎁
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Onboarding Bonus</p>
                    <p className="text-sm text-gray-600">
                      On signup: L1 ₹{(100 * (percentage?.referral_percentage ?? 0) / 100).toFixed(2)} (₹100 × {percentage?.referral_percentage ?? 0}%) + L2 ₹{(100 * (percentage?.second_level_percentage ?? 1) / 100).toFixed(2)} (₹100 × {percentage?.second_level_percentage ?? 1}%)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <ReferralTransactions
            transactions={transactions.transactions || []}
            totalCount={transactions.total_count || 0}
            page={transactionsPage}
            limit={limit}
            onPageChange={(page) => loadTransactions(page)}
            loading={tabLoading}
          />
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <ReferralUsers
            users={users.users || []}
            totalCount={users.total_count || 0}
            page={usersPage}
            limit={limit}
            onPageChange={(page) => loadUsers(page)}
            onViewChain={loadUserChain}
            loading={tabLoading}
          />
        )}

        {/* Sponsors Tab */}
        {activeTab === 'sponsors' && (
          <ReferralSponsors
            sponsors={sponsorsData.sponsors || []}
            totalCount={sponsorsData.total_count || 0}
            page={sponsorsPage}
            limit={limit}
            onPageChange={(page) => loadSponsors(page)}
            loading={tabLoading}
          />
        )}
      </div>

      {/* Chain View Modal */}
      <ReferralChainView
        isOpen={showChainView}
        onClose={() => {
          setShowChainView(false);
          setChainData(null);
          setSelectedUser(null);
        }}
        chainData={chainData}
        loading={chainLoading}
        selectedUser={selectedUser}
      />
    </div>
  );
};

export default Referal;
